/**
 * Real end-to-end encryption via MLS (RFC 9420), using `ts-mls`. Each 1:1
 * conversation is a 2-member MLS group, giving forward secrecy and
 * post-compromise security via epoch key rotation (MLS's equivalent of the
 * Double Ratchet) — unlike the Phase 1 placeholder it replaces.
 *
 * Identity is self-certifying: the MLS Credential's `identity` bytes ARE the
 * long-term Ed25519 signature public key, not the server-assigned userId
 * (which doesn't exist yet when `createIdentity()` runs). Real assurance that
 * a credential belongs to the right person comes from safety-number
 * verification (see `safetyNumber`), matching the approved design — so
 * `ts-mls`'s default, permissive `AuthenticationService` is used as-is.
 */
import { sha256 } from "@noble/hashes/sha256";
import type { SafetyNumber } from "@truewave/protocol";
import {
  createApplicationMessage,
  createCommit,
  createGroup,
  decodeGroupState,
  decodeMlsMessage,
  defaultCapabilities,
  defaultLifetime,
  emptyPskIndex,
  encodeGroupState,
  encodeMlsMessage,
  generateKeyPackageWithKey,
  getCiphersuiteFromName,
  getCiphersuiteImpl,
  joinGroup,
  makePskIndex,
  processPrivateMessage,
  zeroOutUint8Array,
  type CiphersuiteImpl,
  type ClientState,
  type Credential,
  type MLSMessage,
  type PrivateKeyPackage,
  type Proposal,
} from "ts-mls";
import {
  decodeKeyPackage,
  defaultClientConfig,
  encodeCredential,
  encodeKeyPackage,
  makeKeyPackageRef,
} from "./mlsInternal.js";
import { concat, fromB64, toB64, toB64Url, utf8 } from "../lib/bytes.js";
import type {
  CreatedConversation,
  CryptoProvider,
  CryptoSnapshot,
  JoinedConversation,
  NewIdentity,
} from "./types.js";

const CIPHERSUITE_NAME = "MLS_128_DHKEMX25519_AES128GCM_SHA256_Ed25519" as const;
const POOL_SIZE = 20;

interface PrivateKeyPackageB64 {
  initPrivateKey: string;
  hpkePrivateKey: string;
  signaturePrivateKey: string;
}

interface PoolEntryState {
  publicPackage: string; // b64 KeyPackage
  privatePackage: PrivateKeyPackageB64;
  ref: string; // b64 KeyPackageRef, precomputed so incoming Welcomes can be matched
}

interface ConversationState {
  id: string;
  peerUserId: string;
  groupState: string; // b64, RFC-9420 binary encoding of GroupState
}

interface EngineState {
  version: 1;
  userId: string;
  signatureKeyPair: { publicKey: string; signKey: string };
  pool: PoolEntryState[];
  conversations: ConversationState[];
}

function conversationId(a: string, b: string): string {
  const [x, y] = [a, b].sort();
  return toB64Url(sha256(utf8.encode(`tw-conv|${x}|${y}`)));
}

function toPrivateB64(p: PrivateKeyPackage): PrivateKeyPackageB64 {
  return {
    initPrivateKey: toB64(p.initPrivateKey),
    hpkePrivateKey: toB64(p.hpkePrivateKey),
    signaturePrivateKey: toB64(p.signaturePrivateKey),
  };
}

function fromPrivateB64(p: PrivateKeyPackageB64): PrivateKeyPackage {
  return {
    initPrivateKey: fromB64(p.initPrivateKey),
    hpkePrivateKey: fromB64(p.hpkePrivateKey),
    signaturePrivateKey: fromB64(p.signaturePrivateKey),
  };
}

export class MlsCrypto implements CryptoProvider {
  private state: EngineState | null = null;
  private impl: CiphersuiteImpl | null = null;
  private readonly liveGroups = new Map<string, ClientState>();
  private readonly byPeer = new Map<string, string>();

  get ready(): boolean {
    return this.state !== null;
  }

  private engine(): EngineState {
    if (!this.state) throw new Error("crypto engine not initialized");
    return this.state;
  }

  private async cs(): Promise<CiphersuiteImpl> {
    if (!this.impl) this.impl = await getCiphersuiteImpl(getCiphersuiteFromName(CIPHERSUITE_NAME));
    return this.impl;
  }

  private signatureKeyPair(): { publicKey: Uint8Array; signKey: Uint8Array } {
    const e = this.engine();
    return { publicKey: fromB64(e.signatureKeyPair.publicKey), signKey: fromB64(e.signatureKeyPair.signKey) };
  }

  private credential(): Credential {
    return { credentialType: "basic", identity: this.signatureKeyPair().publicKey };
  }

  private reindex(): void {
    this.byPeer.clear();
    for (const c of this.engine().conversations) this.byPeer.set(c.peerUserId, c.id);
  }

  private async loadGroup(id: string): Promise<ClientState> {
    const cached = this.liveGroups.get(id);
    if (cached) return cached;
    const entry = this.engine().conversations.find((c) => c.id === id);
    if (!entry) throw new Error(`unknown conversation ${id}`);
    const decoded = decodeGroupState(fromB64(entry.groupState), 0);
    if (!decoded) throw new Error("corrupt conversation state");
    const state: ClientState = { ...decoded[0], clientConfig: defaultClientConfig };
    this.liveGroups.set(id, state);
    return state;
  }

  private saveGroup(id: string, peerUserId: string, state: ClientState): void {
    this.liveGroups.set(id, state);
    const entry: ConversationState = { id, peerUserId, groupState: toB64(encodeGroupState(state)) };
    const list = this.engine().conversations;
    const i = list.findIndex((c) => c.id === id);
    if (i >= 0) list[i] = entry;
    else list.push(entry);
    this.byPeer.set(peerUserId, id);
  }

  async createIdentity(): Promise<NewIdentity> {
    const cs = await this.cs();
    const signatureKeyPair = await cs.signature.keygen();

    this.state = {
      version: 1,
      userId: "",
      signatureKeyPair: { publicKey: toB64(signatureKeyPair.publicKey), signKey: toB64(signatureKeyPair.signKey) },
      pool: [],
      conversations: [],
    };

    const credential = this.credential();
    const keyPackages = await this.generateKeyPackages(POOL_SIZE);
    return {
      mlsCredential: toB64(encodeCredential(credential)),
      signaturePublicKey: toB64(signatureKeyPair.publicKey),
      keyPackages,
    };
  }

  bindUserId(userId: string): void {
    this.engine().userId = userId;
  }

  async restore(snapshot: CryptoSnapshot): Promise<void> {
    const parsed = JSON.parse(snapshot) as EngineState;
    if (parsed.version !== 1) throw new Error(`unsupported snapshot version ${parsed.version}`);
    this.state = parsed;
    this.liveGroups.clear();
    this.reindex();
  }

  async snapshot(): Promise<CryptoSnapshot> {
    return JSON.stringify(this.engine());
  }

  async generateKeyPackages(count: number): Promise<string[]> {
    const cs = await this.cs();
    const e = this.engine();
    const signatureKeyPair = this.signatureKeyPair();
    const credential = this.credential();
    const out: string[] = [];

    for (let i = 0; i < Math.max(1, count); i++) {
      const { publicPackage, privatePackage } = await generateKeyPackageWithKey(
        credential,
        defaultCapabilities(),
        defaultLifetime,
        [],
        signatureKeyPair,
        cs,
      );
      const ref = await makeKeyPackageRef(publicPackage, cs.hash);
      const publicB64 = toB64(encodeKeyPackage(publicPackage));
      e.pool.push({ publicPackage: publicB64, privatePackage: toPrivateB64(privatePackage), ref: toB64(ref) });
      out.push(publicB64);
    }
    return out;
  }

  async createConversation(peer: {
    userId: string;
    signaturePublicKey: string;
    keyPackage: string;
  }): Promise<CreatedConversation> {
    const cs = await this.cs();
    const e = this.engine();

    const decodedPeer = decodeKeyPackage(fromB64(peer.keyPackage), 0);
    if (!decodedPeer) throw new Error("malformed peer KeyPackage");
    const peerPublicPackage = decodedPeer[0];
    if (toB64(peerPublicPackage.leafNode.signaturePublicKey) !== peer.signaturePublicKey) {
      throw new Error("KeyPackage/identity mismatch");
    }

    // A fresh KeyPackage for our own leaf in this new group. It's never
    // published to the relay, so it's minted ad hoc rather than drawn from
    // the claimable pool.
    const own = await generateKeyPackageWithKey(
      this.credential(),
      defaultCapabilities(),
      defaultLifetime,
      [],
      this.signatureKeyPair(),
      cs,
    );

    const groupId = sha256(utf8.encode(`tw-conv|${[e.userId, peer.userId].sort().join("|")}`));
    let state = await createGroup(groupId, own.publicPackage, own.privatePackage, [], cs);

    const addProposal: Proposal = { proposalType: "add", add: { keyPackage: peerPublicPackage } };
    const commitResult = await createCommit(
      { state, cipherSuite: cs },
      { extraProposals: [addProposal], ratchetTreeExtension: true },
    );
    state = commitResult.newState;
    commitResult.consumed.forEach(zeroOutUint8Array);

    if (!commitResult.welcome) throw new Error("commit produced no Welcome");
    const welcomeMessage: MLSMessage = {
      version: "mls10",
      wireformat: "mls_welcome",
      welcome: commitResult.welcome,
    };

    const id = conversationId(e.userId, peer.userId);
    this.saveGroup(id, peer.userId, state);

    return { conversationId: id, peerUserId: peer.userId, welcome: toB64(encodeMlsMessage(welcomeMessage)) };
  }

  async joinConversation(welcomeB64: string, peerUserId: string): Promise<JoinedConversation> {
    const cs = await this.cs();
    const e = this.engine();

    const decoded = decodeMlsMessage(fromB64(welcomeB64), 0);
    if (!decoded) throw new Error("malformed Welcome");
    const message = decoded[0];
    if (message.wireformat !== "mls_welcome") throw new Error("expected a Welcome message");
    const welcome = message.welcome;

    const refs = new Set(welcome.secrets.map((s) => toB64(s.newMember)));
    const poolIndex = e.pool.findIndex((p) => refs.has(p.ref));
    if (poolIndex === -1) throw new Error("no matching KeyPackage in the local pool for this Welcome");
    const [entry] = e.pool.splice(poolIndex, 1);
    const decodedOwn = decodeKeyPackage(fromB64(entry!.publicPackage), 0);
    if (!decodedOwn) throw new Error("corrupt pooled KeyPackage");

    const state = await joinGroup(
      welcome,
      decodedOwn[0],
      fromPrivateB64(entry!.privatePackage),
      emptyPskIndex,
      cs,
    );

    const id = conversationId(e.userId, peerUserId);
    this.saveGroup(id, peerUserId, state);

    return { conversationId: id, peerUserId };
  }

  conversationFor(peerUserId: string): string | undefined {
    return this.byPeer.get(peerUserId);
  }

  async encrypt(convId: string, plaintext: Uint8Array): Promise<string> {
    const cs = await this.cs();
    const state = await this.loadGroup(convId);
    const result = await createApplicationMessage(state, plaintext, cs);
    result.consumed.forEach(zeroOutUint8Array);

    const peerUserId = this.engine().conversations.find((c) => c.id === convId)!.peerUserId;
    this.saveGroup(convId, peerUserId, result.newState);

    const message: MLSMessage = {
      version: "mls10",
      wireformat: "mls_private_message",
      privateMessage: result.privateMessage,
    };
    return toB64(encodeMlsMessage(message));
  }

  async decrypt(convId: string, ciphertext: string): Promise<Uint8Array> {
    const cs = await this.cs();
    const state = await this.loadGroup(convId);

    const decoded = decodeMlsMessage(fromB64(ciphertext), 0);
    if (!decoded) throw new Error("malformed application message");
    const message = decoded[0];
    if (message.wireformat !== "mls_private_message") throw new Error("expected an application message");

    const result = await processPrivateMessage(state, message.privateMessage, makePskIndex(state, {}), cs);
    result.consumed.forEach(zeroOutUint8Array);

    const peerUserId = this.engine().conversations.find((c) => c.id === convId)!.peerUserId;
    this.saveGroup(convId, peerUserId, result.newState);

    if (result.kind !== "applicationMessage") {
      throw new Error("expected an application message, got a group state change");
    }
    return result.message;
  }

  async safetyNumber(convId: string): Promise<SafetyNumber> {
    const state = await this.loadGroup(convId);
    const myPub = toB64(this.signatureKeyPair().publicKey);

    let peerPub: Uint8Array | undefined;
    for (const node of state.ratchetTree) {
      if (node?.nodeType === "leaf" && toB64(node.leaf.signaturePublicKey) !== myPub) {
        peerPub = node.leaf.signaturePublicKey;
        break;
      }
    }
    if (!peerPub) throw new Error("conversation has no peer");

    const [a, b] = [myPub, toB64(peerPub)].sort();
    const raw = sha256(concat(utf8.encode("tw-safety-mls|"), fromB64(a!), fromB64(b!))).slice(0, 30);
    let digits = "";
    for (let i = 0; i < raw.length; i += 5) {
      const chunk = raw.slice(i, i + 5);
      let v = 0n;
      for (const byte of chunk) v = (v << 8n) | BigInt(byte);
      digits += (v % 10_000_000_000n).toString().padStart(10, "0");
    }
    return { digits, qr: toB64Url(raw) };
  }
}
